import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal, Radio, Segmented, Select, Slider, Switch, Tag, Tooltip, message } from 'antd'
import {
  AimOutlined,
  ArrowLeftOutlined,
  BorderOutlined,
  ClearOutlined,
  CloseOutlined,
  DragOutlined,
  EllipsisOutlined,
  ExpandOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HighlightOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  PushpinOutlined,
  RedoOutlined,
  ReloadOutlined,
  RetweetOutlined,
  SaveOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import {
  editorAutoCutout,
  editorOpen,
  editorSaveTransparentWithRetry,
  listEngines,
  type AssetChainOut,
} from '../../api/client'
import type { EngineInfo } from '../../types'
import DualPaneCutoutEditor, {
  type BgMode,
  type DualPaneEditorHandle,
  type Polarity,
  type ToolShape,
} from './DualPaneCutoutEditor'
import { submitEditorSave } from './editorSaveManager'
import { MODAL_SURFACE_CLASS, useModalScrollLock } from '../../saas/utils/scrollLock'

type Phase = 'idle' | 'loading-model' | 'generating-mask' | 'optimizing-edge' | 'generating-preview' | 'failed'

interface Props {
  chainId: string
  filename: string
  open: boolean
  onClose: () => void
  onSaved: (chain: AssetChainOut) => void
  /** List-row chain already in memory — seed progressive display immediately
   * so the user sees proxy/transparent_proxy via `_deliver` (presigned) while
   * `editor/open` + canvas `/pixels` catch up. */
  initialChain?: AssetChainOut | null
}

/**
 * Single source of truth for "what tool is the user working with" at the
 * UI level. This is intentionally a flat enum (not a `{tool, polarity}`
 * pair) so every consumer — the toolbar's active/inactive state, the
 * properties panel's visibility, the footer label, the brush cursor color
 * — reads the exact same value instead of each recomputing an "is this a
 * brush?" check from a different pair of loosely-related flags.
 */
type EditorTool =
  | 'keep_brush'
  | 'remove_brush'
  | 'positive_point'
  | 'negative_point'
  | 'rect'
  | 'lasso'
  | 'pan'

/** Maps the UI-level tool to the lower-level (tool shape, polarity) pair
 * that `DualPaneCutoutEditor` actually paints with. `rect`/`lasso` carry
 * their own independent add/subtract toggle (`shapePolarity`) since a
 * single click on the toolbar doesn't pick a polarity for them. */
function toShapeAndPolarity(activeTool: EditorTool, shapePolarity: Polarity): { tool: ToolShape; polarity: Polarity } {
  switch (activeTool) {
    case 'keep_brush':
      return { tool: 'brush', polarity: 'add' }
    case 'remove_brush':
      return { tool: 'brush', polarity: 'subtract' }
    case 'positive_point':
      return { tool: 'click', polarity: 'add' }
    case 'negative_point':
      return { tool: 'click', polarity: 'subtract' }
    case 'rect':
      return { tool: 'rect', polarity: shapePolarity }
    case 'lasso':
      return { tool: 'lasso', polarity: shapePolarity }
    case 'pan':
      return { tool: 'pan', polarity: 'add' }
  }
}

/** Small icon shown inline in the always-visible current-tool banner. */
const TOOL_ICON: Record<EditorTool, React.ReactNode> = {
  keep_brush: <HighlightOutlined />,
  remove_brush: <ClearOutlined />,
  positive_point: <AimOutlined />,
  negative_point: <PushpinOutlined />,
  rect: <BorderOutlined />,
  lasso: <EllipsisOutlined />,
  pan: <DragOutlined />,
}

interface ToolDef {
  key: EditorTool
  ariaLabel: string
  icon: React.ReactNode
  toneClass?: string
}

interface ToolGroupDef {
  title: string
  items: ToolDef[]
}

const LAST_BRUSH_TOOL_KEY = 'pic2content.editor.lastBrushTool'

/** First-load default per spec: never a point-select tool. If the user
 * previously used a brush tool in this browser, restore that; otherwise
 * fall back to "去除画笔" (the primary use case — trimming background the
 * auto-cutout over-kept). */
function getInitialTool(): EditorTool {
  try {
    const saved = window.localStorage.getItem(LAST_BRUSH_TOOL_KEY)
    if (saved === 'keep_brush' || saved === 'remove_brush') return saved
  } catch {
    /* localStorage unavailable — fine to ignore */
  }
  return 'remove_brush'
}

export default function ChainEditorModal({
  chainId,
  filename,
  open,
  onClose,
  onSaved,
  initialChain = null,
}: Props) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const toolLabel = (key: EditorTool) => t(`chainEditor.tools.${key}`)
  const toolHint = (key: EditorTool) => t(`chainEditor.toolHints.${key}`)
  const toolTooltip = (key: EditorTool) => t(`chainEditor.toolAria.${key}`)

  const editorRef = useRef<DualPaneEditorHandle>(null)
  // Modal Surface Isolation Layer 1 (VISTOERA_OBJECT_IMAGE_VIEWER_EDITOR_
  // FULL_MODAL_ISOLATION_P0): DualPaneCutoutEditor's own native wheel
  // listener (Layer 2) only covers its two image host panes, so wheeling
  // over the toolbar/sidebar/header — or anywhere before those panes have
  // mounted — previously fell straight through to the page behind this
  // modal. This document-level lock blocks that unconditionally regardless
  // of what Layer 2 covers.
  useModalScrollLock(open)
  // Seed from the list-row chain immediately so progressive display can paint
  // proxy / transparent_proxy before editor/open returns (Object Image Editor UX P0).
  const [chain, setChain] = useState<AssetChainOut | null>(
    initialChain && initialChain.id === chainId ? initialChain : null,
  )
  const [engines, setEngines] = useState<EngineInfo[]>([])
  const [engineName, setEngineName] = useState<string | undefined>()
  const [phase, setPhase] = useState<Phase>('idle')
  const [phaseMessage, setPhaseMessage] = useState('')
  const [phaseError, setPhaseError] = useState('')

  const [maskPath, setMaskPath] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const [activeTool, setActiveToolState] = useState<EditorTool>(getInitialTool)
  const [dragHintTool, setDragHintTool] = useState<'positive_point' | 'negative_point' | null>(null)
  const setActiveTool = useCallback((toolKey: EditorTool) => {
    setActiveToolState(toolKey)
    setDragHintTool(null)
    if (toolKey === 'keep_brush' || toolKey === 'remove_brush') {
      try {
        window.localStorage.setItem(LAST_BRUSH_TOOL_KEY, toolKey)
      } catch {
        /* localStorage unavailable (privacy mode etc.) — fine to ignore */
      }
    }
  }, [])
  const [shapePolarity, setShapePolarity] = useState<Polarity>('add')
  const { tool, polarity } = toShapeAndPolarity(activeTool, shapePolarity)
  const isBrushTool = activeTool === 'keep_brush' || activeTool === 'remove_brush'
  const isShapeTool = activeTool === 'rect' || activeTool === 'lasso'
  const [brushSize, setBrushSize] = useState(60)
  const [brushHardness, setBrushHardness] = useState(0.7)
  const [brushOpacity, setBrushOpacity] = useState(1)
  const [overlayOpacity, setOverlayOpacity] = useState(0.4)
  const [showOverlay, setShowOverlay] = useState(true)
  const [debugMode, setDebugMode] = useState(false)
  const [bgMode, setBgMode] = useState<BgMode>('checker')
  const [dirty, setDirty] = useState(false)
  const [saveSubmitted, setSaveSubmitted] = useState(false)
  /** TASK-00611: distinguish in-flight vs failed so we never show
   * "保存中 / 后台继续" alongside a hard failure, and so Retry works. */
  const [saveFailed, setSaveFailed] = useState(false)
  const [scale, setScale] = useState(1)
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 })
  const [saving, setSaving] = useState(false)
  const [confirmEngineOpen, setConfirmEngineOpen] = useState(false)
  const [canvasReady, setCanvasReady] = useState(false)
  /** True only after `editor/open` returns — guarantees `ensure_proxy` has
   * run on the server before DualPaneCutoutEditor issues its first `/pixels`
   * fetch (Transparent Editor First-Open P0, 2026-09-17). Without this gate
   * the child mounted immediately on list-row `proxy_url` and often fetched
   * before the proxy file existed, silently failing and leaving a blank left
   * pane until the user closed and reopened (second open hit the warm proxy). */
  const [sourceSessionReady, setSourceSessionReady] = useState(false)

  const maskUrl = maskPath ? `${maskPath}${maskPath.includes('?') ? '&' : '?'}v=${reloadToken}` : null

  const runAutoCutout = useCallback(
    async (engine?: string) => {
      setPhase('loading-model')
      setPhaseMessage(t('chainEditor.phase.loadingModel'))
      setPhaseError('')
      try {
        setPhaseMessage(t('chainEditor.phase.generatingMask'))
        setPhase('generating-mask')
        const { data } = await editorAutoCutout(chainId, engine)
        setPhaseMessage(t('chainEditor.phase.generatingPreview'))
        setPhase('generating-preview')
        setChain(data)
        setMaskPath(data.mask_url)
        setReloadToken((t) => t + 1)
        setDirty(false)
      } catch (e) {
        setPhase('failed')
        setPhaseError((e as Error).message)
      }
    },
    [chainId],
  )

  useEffect(() => {
    if (!open) return
    let mounted = true
    setPhase('idle')
    setPhaseError('')
    setDirty(false)
    setSaveSubmitted(false)
    setCanvasReady(false)
    setSourceSessionReady(false)
    // Progressive seed: paint whatever the list already knows before waiting
    // on editor/open (ensure_proxy + mask derivation).
    if (initialChain && initialChain.id === chainId) {
      setChain(initialChain)
      if (initialChain.mask_url && initialChain.transparent_relative_path) {
        setMaskPath(initialChain.mask_url)
      }
    } else {
      setChain(null)
    }
    const tMetaStart = performance.now()
    ;(async () => {
      const [openResp, engResp] = await Promise.all([editorOpen(chainId), listEngines()])
      if (!mounted) return
      // EDITOR_LOAD_TIMING's `metadata_ms` leg (Editor Load Performance P0,
      // 2026-08) — recorded here (the actual API-call site) and read back
      // by `DualPaneCutoutEditor`'s own pixel-load timing log, since that
      // component has no direct visibility into this modal's `editorOpen`/
      // `listEngines` calls.
      if (typeof window !== 'undefined') {
        window.__editorMetadataMs = window.__editorMetadataMs || {}
        window.__editorMetadataMs[chainId] = performance.now() - tMetaStart
      }
      setEngines(engResp.data)
      setEngineName(
        openResp.data.current_matting_engine || engResp.data.find((e) => e.is_default)?.name || engResp.data[0]?.name,
      )
      setChain(openResp.data)
      // Proxy file is now guaranteed on disk — safe to mount the pixel loader.
      setSourceSessionReady(true)
      if (openResp.data.loaded_existing_mask && openResp.data.mask_url) {
        setMaskPath(openResp.data.mask_url)
        setReloadToken((t) => t + 1)
      } else {
        await runAutoCutout(openResp.data.current_matting_engine || undefined)
      }
    })()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chainId])

  useEffect(() => {
    if (!dragHintTool) return
    const timer = window.setTimeout(() => setDragHintTool(null), 8000)
    return () => window.clearTimeout(timer)
  }, [dragHintTool])

  const requestClose = () => {
    // A submitted save already owns its lifecycle outside this component —
    // closing must not warn "unsaved" once handoff succeeded.
    if (dirty && !saveSubmitted && !window.confirm(t('chainEditor.discardLeaveConfirm'))) return
    onClose()
  }

  const onSave = async () => {
    if (!editorRef.current || saving || saveSubmitted) return
    setSaving(true)
    setSaveFailed(false)
    try {
      // Prepare the payload while the editor is still mounted, then hand
      // the actual upload+persist to the module-level save manager so the
      // task survives this modal closing (Async Save P0).
      const maskBlob = await editorRef.current.exportMaskBlob()
      const transparentPng = await editorRef.current.exportTransparentPngBlob()
      const accepted = submitEditorSave({
        kind: 'transparent',
        chainId,
        run: () => editorSaveTransparentWithRetry(chainId, maskBlob, transparentPng),
        onSaved: (data) => {
          setChain(data)
          setSaveSubmitted(false)
          setSaveFailed(false)
          setDirty(false)
          onSaved(data)
        },
        onFailure: () => {
          // Keep brush/mask edits; clear contradictory "saving/submitted"
          // tags and re-enable the save button for an explicit retry.
          setSaveSubmitted(false)
          setSaveFailed(true)
          setDirty(true)
        },
        successMessage: t('chainEditor.saveSuccess'),
      })
      if (accepted) {
        setSaveSubmitted(true)
        setDirty(false)
      }
    } catch (e) {
      setSaveFailed(true)
      setDirty(true)
      message.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const confirmRerun = () => {
    setConfirmEngineOpen(false)
    runAutoCutout(engineName)
  }

  const isBusy = phase !== 'idle' && phase !== 'failed'
  // When progressive display URLs are available, hide the blocking spinner —
  // the user already sees proxy/transparent_proxy while canvas pixels load.
  const hasProgressiveRight =
    !!(chain?.transparent_proxy_url || chain?.transparent_url || chain?.transparent_thumb_url)
  const showRightBusyOverlay = isBusy || (!canvasReady && !hasProgressiveRight)

  // Three visually distinct icon *families* so the four "point-ish" tools
  // can never again be mistaken for variations of the same glyph:
  // brush = pen/eraser, AI point = target/pin, region/view = shape/hand.
  // Each group also carries its own short title, rendered above a visible
  // divider — see `.editor-tool-group-title` — so grouping doesn't rely on
  // spacing/tooltips alone.
  const toolGroups: ToolGroupDef[] = useMemo(
    () => [
      {
        title: t('chainEditor.groups.brush'),
        items: [
          { key: 'keep_brush', ariaLabel: toolLabel('keep_brush'), icon: <HighlightOutlined /> },
          { key: 'remove_brush', ariaLabel: toolLabel('remove_brush'), icon: <ClearOutlined />, toneClass: 'danger-tone' },
        ],
      },
      {
        title: t('chainEditor.groups.aiPoint'),
        items: [
          { key: 'positive_point', ariaLabel: toolLabel('positive_point'), icon: <AimOutlined /> },
          { key: 'negative_point', ariaLabel: toolLabel('negative_point'), icon: <PushpinOutlined />, toneClass: 'danger-tone' },
        ],
      },
      {
        title: t('chainEditor.groups.region'),
        items: [
          { key: 'rect', ariaLabel: toolLabel('rect'), icon: <BorderOutlined /> },
          { key: 'lasso', ariaLabel: toolLabel('lasso'), icon: <EllipsisOutlined /> },
        ],
      },
      {
        title: t('chainEditor.groups.view'),
        items: [{ key: 'pan', ariaLabel: toolLabel('pan'), icon: <DragOutlined /> }],
      },
    ],
    [t, toolLabel],
  )

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      footer={null}
      closable={false}
      width="96vw"
      centered
      style={{ paddingBottom: 0 }}
      styles={{ body: { height: '92vh', padding: 0 }, mask: { backgroundColor: 'transparent' } }}
      wrapClassName={MODAL_SURFACE_CLASS}
      destroyOnClose
    >
      <div className="editor-shell-v2">
        <header className="editor-header-v2">
          <div className="editor-header-section editor-header-left">
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={requestClose}>
              {t('chainEditor.backToList')}
            </Button>
            <span className="editor-filename-v2" title={filename}>
              {filename}
            </span>
            {chain && (
              <Tag color={chain.transparent_status === 'done' ? 'success' : 'default'} className="editor-header-tag">
                {chain.transparent_status_label}
              </Tag>
            )}
            {dirty && !saveSubmitted && !saveFailed && (
              <Tag color="gold" className="editor-header-tag">
                {t('chainEditor.unsaved')}
              </Tag>
            )}
            {saveFailed && !saveSubmitted && (
              <Tag color="error" className="editor-header-tag" data-testid="cutout-save-failed-tag">
                {t('chainEditor.footerSaveFailed')}
              </Tag>
            )}
            {saveSubmitted && (
              <Tag color="blue" className="editor-header-tag" data-testid="cutout-save-submitted-tag">
                {t('chainEditor.saving')}
              </Tag>
            )}
          </div>

          <div className="editor-header-section editor-header-center">
            <Select
              size="small"
              style={{ width: 200 }}
              value={engineName}
              onChange={setEngineName}
              options={engines.map((e) => ({
                value: e.name,
                label: `${e.description || e.name}${e.available ? '' : t('chainEditor.engineUnavailable')}`,
                disabled: !e.available,
              }))}
            />
            <Tooltip title={t('chainEditor.rerunAutoCutout')}>
              <Button size="small" icon={<ReloadOutlined />} disabled={isBusy} onClick={() => setConfirmEngineOpen(true)}>
                {t('chainEditor.rerunAutoCutout')}
              </Button>
            </Tooltip>
          </div>

          <div className="editor-header-section editor-header-right">
            <Tooltip title={t('chainEditor.undo')}>
              <Button size="small" icon={<UndoOutlined />} onClick={() => editorRef.current?.undo()} />
            </Tooltip>
            <Tooltip title={t('chainEditor.redo')}>
              <Button size="small" icon={<RedoOutlined />} onClick={() => editorRef.current?.redo()} />
            </Tooltip>
            <Tooltip title={t('chainEditor.reset')}>
              <Button size="small" onClick={() => editorRef.current?.clearPendingEdits()}>
                {t('chainEditor.reset')}
              </Button>
            </Tooltip>
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} disabled={isBusy || saveSubmitted} onClick={onSave}>
              {t('chainEditor.saveTransparent')}
            </Button>
          </div>
        </header>

        {phase === 'failed' && (
          <div className="editor-status-banner editor-status-error">
            {t('chainEditor.autoCutoutFailed', { error: phaseError })}
            <Button size="small" style={{ marginLeft: 12 }} onClick={() => runAutoCutout(engineName)}>
              {t('actions:retry')}
            </Button>
          </div>
        )}

        <div className="editor-body-v2">
          <aside className="editor-toolbar-v2" data-testid="editor-toolbar">
            {toolGroups.map((group) => (
              <div className="editor-tool-group" key={group.title}>
                <div className="editor-tool-group-title">{group.title}</div>
                <div className="editor-tool-group-items">
                  {group.items.map((item) => (
                    <Tooltip key={item.key} title={toolTooltip(item.key)} placement="right">
                      <button
                        type="button"
                        data-testid={`tool-btn-${item.key}`}
                        className={`editor-tool-btn${activeTool === item.key ? ' active' : ''}${item.toneClass ? ` ${item.toneClass}` : ''}`}
                        aria-label={item.ariaLabel}
                        title={item.ariaLabel}
                        onClick={() => setActiveTool(item.key)}
                      >
                        {item.icon}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              </div>
            ))}
            <div className="editor-tool-group">
              <div className="editor-tool-group-title">{t('chainEditor.groups.other')}</div>
              <div className="editor-tool-group-items">
                <Tooltip title={t('chainEditor.invert')} placement="right">
                  <button type="button" className="editor-tool-btn" aria-label={t('chainEditor.invert')} onClick={() => editorRef.current?.invertSelection()}>
                    <RetweetOutlined />
                  </button>
                </Tooltip>
              </div>
            </div>
          </aside>

          <div className="editor-canvas-v2">
            {/* Always-visible current-tool status, anchored near the left
                pane's "原图与选区" heading — deliberately NOT inside
                DualPaneCutoutEditor.tsx (which stays untouched for this
                purpose) so the verified canvas/pointer code needed no
                changes to support it. `pointer-events: none` so it never
                sits in front of the drawing surface. */}
            <div className="editor-current-tool-banner" data-testid="current-tool-banner">
              <span className="editor-current-tool-icon">{TOOL_ICON[activeTool]}</span>
              <span className="editor-current-tool-name" data-testid="current-tool-name">
                {t('chainEditor.currentTool', { tool: toolLabel(activeTool) })}
              </span>
              <span className="editor-current-tool-hint" data-testid="current-tool-hint">
                {toolHint(activeTool)}
              </span>
            </div>
            {(dragHintTool === 'positive_point' || dragHintTool === 'negative_point') && (
              <div className="editor-drag-hint" data-testid="click-drag-hint">
                <span>
                  {t('chainEditor.clickOnlyHint', {
                    tool: toolLabel(dragHintTool),
                    brush: toolLabel(dragHintTool === 'positive_point' ? 'keep_brush' : 'remove_brush'),
                  })}
                </span>
                <Button
                  size="small"
                  type="primary"
                  data-testid="switch-to-brush-btn"
                  onClick={() => setActiveTool(dragHintTool === 'positive_point' ? 'keep_brush' : 'remove_brush')}
                >
                  {t('chainEditor.switchToBrush', {
                    brush: toolLabel(dragHintTool === 'positive_point' ? 'keep_brush' : 'remove_brush'),
                  })}
                </Button>
                <button
                  type="button"
                  className="editor-drag-hint-close"
                  aria-label={t('chainEditor.closeHint')}
                  onClick={() => setDragHintTool(null)}
                >
                  <CloseOutlined />
                </button>
              </div>
            )}
            {sourceSessionReady && chain?.proxy_url && (
              <DualPaneCutoutEditor
                ref={editorRef}
                proxyUrl={chain.proxy_url}
                displayLeftUrl={chain.proxy_url}
                displayRightUrl={
                  chain.transparent_proxy_url || chain.transparent_url || chain.transparent_thumb_url || null
                }
                maskUrl={maskUrl}
                chainId={chainId}
                reloadToken={reloadToken}
                tool={tool}
                polarity={polarity}
                brushSize={tool === 'click' ? 18 : brushSize}
                brushHardness={tool === 'click' ? 0.85 : brushHardness}
                brushOpacity={brushOpacity}
                overlayOpacity={overlayOpacity}
                showOverlay={showOverlay}
                bgMode={bgMode}
                debugMode={debugMode}
                onDirtyChange={setDirty}
                onScaleChange={setScale}
                onImageSize={(w, h) => setImgSize({ w, h })}
                onReady={() => {
                  setCanvasReady(true)
                  setPhase('idle')
                }}
                onClickToolDragAttempt={() => {
                  if (activeTool === 'positive_point' || activeTool === 'negative_point') {
                    setDragHintTool(activeTool)
                  }
                }}
              />
            )}
            {showRightBusyOverlay && (
              <div className="canvas-busy-right">
                <LoadingOutlined spin style={{ fontSize: 22 }} />
                <span>{phaseMessage || t('chainEditor.phase.loadingCanvas')}</span>
              </div>
            )}
            {(!sourceSessionReady || !chain?.proxy_url) && !phaseError && (
              <div className="canvas-busy-left">
                <LoadingOutlined spin style={{ fontSize: 22 }} />
                <span>{t('chainEditor.phase.preparingPreview')}</span>
              </div>
            )}
          </div>

          <aside className="editor-properties-v2">
            {isBrushTool && (
              <div className="prop-section" data-testid="brush-settings">
                <div className="prop-section-title">
                  {t('chainEditor.brushSettings')}
                  <span className={`prop-tool-badge ${activeTool === 'keep_brush' ? 'tone-add' : 'tone-subtract'}`}>
                    {t('chainEditor.currentTool', { tool: toolLabel(activeTool) })}
                  </span>
                </div>
                <div className="prop-row">
                  <div className="prop-row-head">
                    <span className="prop-label">{t('chainEditor.size')}</span>
                    <span className="prop-value">{brushSize} px</span>
                  </div>
                  <Slider min={2} max={500} value={brushSize} onChange={setBrushSize} />
                </div>
                <div className="prop-row">
                  <div className="prop-row-head">
                    <span className="prop-label">{t('chainEditor.hardness')}</span>
                    <span className="prop-value">{Math.round(brushHardness * 100)}%</span>
                  </div>
                  <Slider min={0} max={1} step={0.05} value={brushHardness} onChange={setBrushHardness} />
                </div>
                <div className="prop-row">
                  <div className="prop-row-head">
                    <span className="prop-label">{t('chainEditor.opacity')}</span>
                    <span className="prop-value">{Math.round(brushOpacity * 100)}%</span>
                  </div>
                  <Slider min={0.1} max={1} step={0.05} value={brushOpacity} onChange={setBrushOpacity} />
                </div>
              </div>
            )}

            {isShapeTool && (
              <div className="prop-section">
                <div className="prop-section-title">{t('chainEditor.selectionMode')}</div>
                <Radio.Group
                  size="small"
                  value={shapePolarity}
                  onChange={(e) => setShapePolarity(e.target.value)}
                  optionType="button"
                  style={{ width: '100%' }}
                >
                  <Radio.Button value="add" style={{ width: '50%', textAlign: 'center' }}>
                    {t('chainEditor.add')}
                  </Radio.Button>
                  <Radio.Button value="subtract" style={{ width: '50%', textAlign: 'center' }}>
                    {t('chainEditor.subtract')}
                  </Radio.Button>
                </Radio.Group>
              </div>
            )}

            <div className="prop-section">
              <div className="prop-section-title">{t('chainEditor.selectionDisplay')}</div>
              <div className="prop-row">
                <span className="prop-label">{t('chainEditor.selectionOpacity')}</span>
                <Slider min={0.1} max={0.9} step={0.05} value={overlayOpacity} onChange={setOverlayOpacity} />
              </div>
              <div className="prop-row prop-row-between">
                <span className="prop-label">{t('chainEditor.showSelectionOverlay')}</span>
                <Switch
                  size="small"
                  checked={showOverlay}
                  onChange={setShowOverlay}
                  checkedChildren={<EyeOutlined />}
                  unCheckedChildren={<EyeInvisibleOutlined />}
                />
              </div>
            </div>

            <div className="prop-section">
              <div className="prop-section-title">{t('chainEditor.previewBackground')}</div>
              <Segmented
                block
                size="small"
                value={bgMode}
                onChange={(v) => setBgMode(v as BgMode)}
                options={[
                  { label: t('chainEditor.bgChecker'), value: 'checker' },
                  { label: t('chainEditor.bgWhite'), value: 'white' },
                  { label: t('chainEditor.bgBlack'), value: 'black' },
                  { label: t('chainEditor.bgGray'), value: 'gray' },
                ]}
              />
            </div>

            <div className="prop-section">
              <div className="prop-section-title">{t('chainEditor.groups.view')}</div>
              <div className="prop-row prop-row-between">
                <span className="prop-label">{t('chainEditor.zoom')}</span>
                <span className="prop-value">{Math.round(scale * 100)}%</span>
              </div>
              <div className="prop-view-buttons">
                <Button size="small" onClick={() => editorRef.current?.fitToView()}>
                  {t('chainEditor.fitWindow')}
                </Button>
                <Button size="small" onClick={() => editorRef.current?.zoomTo(1)}>
                  100%
                </Button>
              </div>
            </div>

            <div className="prop-section">
              <div className="prop-row prop-row-between">
                <span className="prop-label">{t('chainEditor.debugInfo')}</span>
                <Switch size="small" checked={debugMode} onChange={setDebugMode} />
              </div>
            </div>
          </aside>
        </div>

        <footer className="editor-footer-v2">
          <div className="footer-section footer-left">
            <span>{imgSize.w}×{imgSize.h}</span>
            <span className="footer-sep" />
            <span>{t('chainEditor.engine', { name: chain?.current_matting_engine || '-' })}</span>
            <span className="footer-sep" />
            <span>{t('chainEditor.tool', { tool: toolLabel(activeTool) })}</span>
          </div>
          <div className="footer-section footer-center">
            {isBusy && (
              <span className="footer-status">
                <LoadingOutlined spin /> {phaseMessage || t('chainEditor.phase.processing')}
              </span>
            )}
            {!isBusy && dirty && !saveSubmitted && !saveFailed && (
              <span className="footer-status footer-status-dirty">{t('chainEditor.footerDirty')}</span>
            )}
            {!isBusy && saveSubmitted && (
              <span className="footer-status footer-status-dirty">{t('chainEditor.footerSubmitted')}</span>
            )}
            {!isBusy && saveFailed && !saveSubmitted && (
              <span className="footer-status footer-status-dirty" data-testid="cutout-save-failed-footer">
                {t('chainEditor.footerSaveFailed')}
              </span>
            )}
            {!isBusy && !dirty && !saveSubmitted && !saveFailed && canvasReady && (
              <span className="footer-status footer-status-ok">{t('chainEditor.footerLoaded')}</span>
            )}
          </div>
          <div className="footer-section footer-right">
            <Button size="small" icon={<MinusOutlined />} onClick={() => editorRef.current?.zoomTo(scale / 1.2)} />
            <Button size="small" icon={<ExpandOutlined />} onClick={() => editorRef.current?.fitToView()} />
            <Button size="small" icon={<PlusOutlined />} onClick={() => editorRef.current?.zoomTo(scale * 1.2)} />
          </div>
        </footer>
      </div>

      <Modal title={t('chainEditor.rerunConfirmTitle')} open={confirmEngineOpen} onOk={confirmRerun} onCancel={() => setConfirmEngineOpen(false)} okText={t('chainEditor.continue')} cancelText={t('actions:cancel')}>
        {t('chainEditor.rerunConfirmContent')}
      </Modal>
    </Modal>
  )
}

